import type { ProblemDetails } from '@/types/problemDetails';

export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails | null;

  constructor(status: number, problem: ProblemDetails | null, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
  }
}

/**
 * The user's Firebase sign-in is fine; they are not provisioned for the
 * dashboard, or their access was revoked.
 *
 * Terminal. Do not sign out, do not retry, do not redirect to sign-in — the
 * sign-in would succeed and land them right back here, forever. Invite-only
 * onboarding means the Firebase project and the backend's user table are
 * populated by two separate actions, so this is a normal state, not an edge case.
 */
export class AccessNotProvisionedError extends ApiError {
  constructor(problem: ProblemDetails | null) {
    super(401, problem, 'Access has not been provisioned for this account.');
    this.name = 'AccessNotProvisionedError';
  }
}

/**
 * Signed in and provisioned, but the token carries no second factor. Route to
 * enrollment.
 *
 * Like AccessNotProvisionedError: do not sign out, do not retry. Signing in
 * again produces the same token and the same answer.
 */
export class SecondFactorEnrollmentRequiredError extends ApiError {
  constructor(problem: ProblemDetails | null) {
    super(401, problem, 'A second factor must be enrolled before using the dashboard.');
    this.name = 'SecondFactorEnrollmentRequiredError';
  }
}

/**
 * Signed in with the SMS recovery factor. SMS sign-in grants no API access; it
 * only permits replacing the authenticator. Route to re-enrollment; do not
 * sign out, do not retry.
 */
export class TotpReenrollmentRequiredError extends ApiError {
  constructor(problem: ProblemDetails | null) {
    super(401, problem, 'The authenticator app must be replaced before using the dashboard.');
    this.name = 'TotpReenrollmentRequiredError';
  }
}

/** The session is gone. Route to sign-in. */
export class SessionLostError extends ApiError {
  constructor(problem: ProblemDetails | null) {
    super(401, problem, 'The session is no longer valid.');
    this.name = 'SessionLostError';
  }
}

/**
 * The role may not perform this action. Render in place; do NOT sign out — a
 * 403 means the app offered a control the role cannot use, which is a bug in
 * its own navigation, and signing out hides it.
 */
export class NotPermittedError extends ApiError {
  constructor(problem: ProblemDetails | null) {
    super(403, problem, 'This action is not permitted for your role.');
    this.name = 'NotPermittedError';
  }
}

/** The backend's `type` for a 400 that carries per-field errors in `fields`. */
export const VALIDATION_FAILED_TYPE = 'validation_failed';

export interface FieldError {
  name: string;
  code: string;
  /**
   * User-facing text, when the backend has some — written for the user and
   * already saying what is wrong, so it is shown as-is. Absent otherwise; fall
   * back to your own copy keyed on `name` + `code`.
   */
  detail?: string;
}

/**
 * The per-field errors of a `validation_failed` response — `problem.fields`,
 * written by the backend's `GlobalExceptionHandler` as `[{ name, code, detail? }]`.
 * Empty for anything else, including a malformed `fields` value, so callers
 * can always fall back to a generic message.
 */
export function fieldErrors(error: unknown): FieldError[] {
  if (!(error instanceof ApiError)) return [];
  const problem = error.problem;
  if (problem?.type !== VALIDATION_FAILED_TYPE || !Array.isArray(problem.fields)) return [];
  return problem.fields.flatMap((field: unknown) => {
    if (typeof field !== 'object' || field === null) return [];
    const { name, code, detail } = field as Record<string, unknown>;
    if (typeof name !== 'string' || typeof code !== 'string') return [];
    return [typeof detail === 'string' && detail !== '' ? { name, code, detail } : { name, code }];
  });
}

function findFieldError(error: unknown, name: string, code: string): FieldError | undefined {
  return fieldErrors(error).find(
    (field) => field.name.toLowerCase() === name.toLowerCase() && field.code === code,
  );
}

/** True when `error` has a field error for `name` with `code`. Field names are compared case-insensitively. */
export function hasFieldError(error: unknown, name: string, code: string): boolean {
  return findFieldError(error, name, code) !== undefined;
}

/** The `detail` of the field error for `name` with `code`, when it has one. */
export function fieldErrorDetail(error: unknown, name: string, code: string): string | undefined {
  return findFieldError(error, name, code)?.detail;
}

export function isRetryableApiError(error: unknown): boolean {
  if (error instanceof ApiError) return error.status >= 500;
  return error instanceof TypeError; // fetch network failure
}
